import type { CSSProperties, HTMLAttributes } from "react";

/**
 * TheCore Studio — Avatar.
 * Round user avatar — photo or auto-tinted initials derived from `name`.
 * The tint + initials are deterministic per name.
 */
export interface AvatarProps extends Omit<HTMLAttributes<HTMLDivElement>, "style"> {
  /** Image URL. Falls back to initials when absent. */
  src?: string;
  /** Full name — drives initials + deterministic tint. */
  name?: string;
  /** Pixel diameter. @default 40 */
  size?: number;
  /** Terracotta focus ring. */
  ring?: boolean;
  style?: CSSProperties;
}

const TINTS = [
  "var(--terracotta-100)",
  "var(--honey-100)",
  "var(--sage-100)",
  "var(--slate-100)",
  "var(--clay-200)",
];
const FGS = [
  "var(--terracotta-700)",
  "var(--honey-700)",
  "var(--sage-700)",
  "var(--slate-700)",
  "var(--clay-700)",
];

function initialsOf(name: string): string {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}

export function Avatar({ src, name = "", size = 40, ring = false, style = {}, ...rest }: AvatarProps) {
  const initials = initialsOf(name);
  const idx = name ? name.charCodeAt(0) % TINTS.length : 0;

  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "var(--radius-pill)",
        background: src ? `center/cover no-repeat url(${src})` : TINTS[idx],
        color: FGS[idx],
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        font: `var(--fw-bold) ${Math.round(size * 0.36)}px/1 var(--font-body)`,
        boxShadow: ring ? "0 0 0 2px var(--bg-raised), 0 0 0 4px var(--brand)" : "none",
        flex: "none",
        overflow: "hidden",
        ...style,
      }}
      {...rest}
    >
      {!src && (initials || "?")}
    </div>
  );
}
